import type { ApolloError } from '@apollo/client';

/**
 * Extracts the GraphQL error or network error from an ApolloError.
 * ApolloError can contain many types of errors, this extracts only the ones we care about.
 * @param apolloError - The ApolloError to extract the error from.
 */
export const getErrorFromApolloError = (apolloError: ApolloError) => {
  const graphQLError = apolloError.graphQLErrors?.[0];
  const networkError = apolloError.networkError;
  return graphQLError ?? networkError ?? null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const getStringMessage = (value: Record<string, unknown>): string | undefined =>
  typeof value.message === 'string' ? value.message : undefined;

/**
 * Serializes an arbitrary caught error into a human-readable string for Sentry.
 *
 * Naively stringifying a caught value produces "[object Object]" whenever a caller
 * passes a plain object (e.g. a GraphQL formatted error or an Apollo error payload),
 * which makes Sentry reports untriageable. This normalizes the common Coach error
 * shapes into a meaningful message:
 *
 * 1. `Error` instances (incl. `ApolloError`, `GraphQLError`) → `error.message`
 * 2. Apollo/GraphQL error shapes → the first GraphQL error / network error message
 * 3. Plain objects → safe `JSON.stringify` (falls back on circular structures)
 * 4. Everything else → `String(value)`
 */
export const serializeError = (error: unknown): string => {
  if (isRecord(error)) {
    // Apollo aggregate shape: surface the underlying GraphQL or network message.
    const graphQLErrors = error.graphQLErrors;
    if (Array.isArray(graphQLErrors) && isRecord(graphQLErrors[0])) {
      const message = getStringMessage(graphQLErrors[0]);
      if (message) return message;
    }
    if (isRecord(error.networkError)) {
      const message = getStringMessage(error.networkError);
      if (message) return message;
    }

    // Error instances and GraphQL formatted errors both carry a string `message`.
    const message = getStringMessage(error);
    if (message) return message;

    // Plain object with no message: stringify it rather than emit "[object Object]".
    try {
      const json = JSON.stringify(error);
      if (json && json !== '{}') return json;
    } catch {
      // Circular or otherwise non-serializable — fall through to String().
    }
  }

  return String(error);
};
